import * as React from 'react'
import { Link, useSearchParams } from 'react-router'
import { Loader2, RotateCcw, Send, Webhook } from 'lucide-react'
import type { WebhookDeliveryDto, WebhookTestResultDto } from '@lib/dto/webhook-delivery.dto'
import { webhookDeliveryStatusSchema, type WebhookDeliveryStatus } from '@lib/enums/webhook-delivery-status.enum'
import {
  ENVELOPE_WEBHOOK_EVENTS,
  WEBHOOK_EVENTS,
  envelopeWebhookEventNameSchema,
  webhookEventNameSchema,
  type EnvelopeWebhookEventName,
} from '@lib/enums/webhook-event.enum'
import { webhookDeliveryQuerySchema, type WebhookDeliveryQuery } from '@lib/input/webhook-delivery.input'
import { uuidParamSchema, type Uuid } from '@lib/validators/params'
import { PageHeader } from '@/components/PageHeader'
import { RecordFacts, RecordHeadline, RecordSection, RecordSheet } from '@/components/RecordSheet'
import { EmptyState, ErrorState, LoadingState } from '@/components/PageState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { Select } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { toast } from '@/components/ui/toast'
import { canEditSettings, useDashboardScope } from '@/dashboard/DashboardScope'
import { ReadOnlyBanner } from '@/dashboard/ReadOnlyBanner'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useWebhookActions, useWebhookDeliveries, useWebhookDelivery } from '@/hooks/useWebhookLog'
import { cn } from '@/lib/utils'

const STATUS_TONES: Record<WebhookDeliveryStatus, { label: string; tone: 'neutral' | 'success' | 'danger' }> = {
  pending: { label: 'Retrying', tone: 'neutral' },
  delivered: { label: 'Delivered', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
}

const FILTER_KEYS = ['envelopeId', 'event', 'status', 'before'] as const

/**
 * The filters in the URL. A bad or unknown parameter (a hand-edited or stale
 * link) is dropped on its own, so the rest of the search still applies.
 * `envelopeId` has to be a uuid; the shared query schema is what decides.
 */
export function filtersFrom(params: URLSearchParams): WebhookDeliveryQuery {
  const raw: Record<string, string> = {}
  for (const key of FILTER_KEYS) {
    const value = params.get(key)
    if (value) raw[key] = value
  }
  for (let attempt = 0; attempt < FILTER_KEYS.length; attempt += 1) {
    const parsed = webhookDeliveryQuerySchema.safeParse(raw)
    if (parsed.success) return parsed.data
    for (const issue of parsed.error.issues) {
      const keys = issue.code === 'unrecognized_keys' ? issue.keys : [issue.path[0]]
      for (const key of keys) {
        if (typeof key === 'string') delete raw[key]
      }
    }
  }
  return {}
}

interface DeliveryViewerProps {
  id: Uuid
  canReplay: boolean
  envelopeLink: (envelopeId: string) => string
}

/** One delivery: where it went, what came back, and the exact payload. */
function DeliveryViewer({ id, canReplay, envelopeLink }: DeliveryViewerProps) {
  const query = useWebhookDelivery(id)
  const actions = useWebhookActions()

  async function replay(): Promise<void> {
    try {
      const result = await actions.replay.mutateAsync(id)
      if (result.status === 'delivered') {
        toast.success(`Delivered (HTTP ${String(result.lastStatusCode ?? '')})`)
      } else {
        toast.error(result.lastError ?? 'The receiver did not accept it')
      }
    } catch (failure) {
      toast.error(apiErrorMessage(failure, 'Could not replay it.'))
    }
  }

  if (query.isLoading) return <LoadingState />
  if (query.isError || !query.data) {
    return (
      <div className="p-card">
        <ErrorState message={apiErrorMessage(query.error, 'Could not load the delivery.')} />
      </div>
    )
  }
  const delivery = query.data
  const status = STATUS_TONES[delivery.status]
  return (
    <>
      <RecordHeadline
        facts={
          <RecordFacts>
            <dt className="text-muted-foreground">Event id</dt>
            <dd className="truncate font-mono">{delivery.id}</dd>
            <dt className="text-muted-foreground">Envelope</dt>
            <dd className="truncate">
              {delivery.envelopeId === null ? (
                <span className="text-muted-foreground">None: an organization event</span>
              ) : (
                <Link to={envelopeLink(delivery.envelopeId)} className="font-mono text-link hover:underline">
                  {delivery.envelopeId}
                </Link>
              )}
            </dd>
            <dt className="text-muted-foreground">URL</dt>
            <dd className="truncate font-mono">{delivery.url}</dd>
            <dt className="text-muted-foreground">Attempts</dt>
            <dd>
              {delivery.attemptCount}
              {delivery.lastAttemptAt ? ` · last ${new Date(delivery.lastAttemptAt).toLocaleString()}` : ''}
            </dd>
            <dt className="text-muted-foreground">Last response</dt>
            <dd className={cn(delivery.lastError ? 'text-danger' : null)}>
              {delivery.lastError ?? (delivery.lastStatusCode ? `HTTP ${String(delivery.lastStatusCode)}` : '—')}
            </dd>
          </RecordFacts>
        }
      >
        <h3 className="font-mono text-body-lg font-semibold">{delivery.event}</h3>
        <Badge tone={status.tone}>{status.label}</Badge>
        {canReplay ? (
          <Button variant="secondary" size="sm" className="ml-auto" disabled={actions.replay.isPending} onClick={() => void replay()}>
            {actions.replay.isPending ? <Loader2 className="animate-spin" /> : <RotateCcw />}
            Replay
          </Button>
        ) : null}
      </RecordHeadline>
      <RecordSection title="Payload">
        <pre className="overflow-x-auto font-mono text-caption">{JSON.stringify(delivery.payload, null, 2)}</pre>
      </RecordSection>
    </>
  )
}

/** Test mode only: a synthetic event to the test webhook URL, with the receiver's answer. */
function TestEventCard() {
  const actions = useWebhookActions()
  const [event, setEvent] = React.useState<EnvelopeWebhookEventName>('envelope.completed')
  const [result, setResult] = React.useState<WebhookTestResultDto | null>(null)

  async function send(): Promise<void> {
    try {
      setResult(await actions.sendTestEvent.mutateAsync(event))
    } catch (failure) {
      toast.error(apiErrorMessage(failure, 'Could not send the test event.'))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Send a test event</CardTitle>
        <CardDescription>
          A synthetic event (marked <span className="font-mono">synthetic: true</span>, no real envelope) to your test
          webhook URL, signed with your test secret. Nothing is stored.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Event" htmlFor="test-event" className="w-60">
            <Select
              id="test-event"
              value={event}
              onChange={(change) => {
                const next = envelopeWebhookEventNameSchema.safeParse(change.target.value)
                if (next.success) setEvent(next.data)
              }}
            >
              {ENVELOPE_WEBHOOK_EVENTS.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </Field>
          <Button onClick={() => void send()} disabled={actions.sendTestEvent.isPending}>
            {actions.sendTestEvent.isPending ? <Loader2 className="animate-spin" /> : <Send />}
            Send
          </Button>
        </div>
        {result ? (
          <div className="space-y-2 rounded-md border border-border bg-subtle p-3 text-caption">
            <p className="flex flex-wrap items-center gap-2">
              <Badge tone={result.delivered ? 'success' : 'danger'}>{result.delivered ? 'Accepted' : 'Not accepted'}</Badge>
              <span>
                {result.statusCode ? `HTTP ${String(result.statusCode)}` : 'No response'} · {result.durationMs} ms
              </span>
            </p>
            {result.error && !result.delivered ? <p className="text-danger">{result.error}</p> : null}
            {result.responseExcerpt ? (
              <pre className="overflow-auto font-mono whitespace-pre-wrap">{result.responseExcerpt}</pre>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

/**
 * Every webhook PixelSign sent (or is retrying) in the mode on screen, with
 * filters, the exact payload, and replay. Test mode adds synthetic test events.
 */
export function WebhooksPage() {
  const scope = useDashboardScope()
  const [params, setParams] = useSearchParams()
  const filters = React.useMemo(() => filtersFrom(params), [params])
  const deliveryParsed = uuidParamSchema.safeParse(params.get('delivery'))
  const selectedId = deliveryParsed.success ? deliveryParsed.data : null
  const query = useWebhookDeliveries(filters)
  const canAct = canEditSettings(scope)

  function setParam(key: keyof WebhookDeliveryQuery, value: string | null): void {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    next.delete('delivery')
    // A new filter starts from the newest page again.
    if (key !== 'before') next.delete('before')
    setParams(next)
  }

  const select = React.useCallback(
    (id: string | null, options: { replace: boolean } = { replace: false }) =>
      setParams((current) => {
        const next = new URLSearchParams(current)
        if (id) next.set('delivery', id)
        else next.delete('delivery')
        return next
      }, options),
    [setParams],
  )

  function renderList(deliveries: WebhookDeliveryDto[]) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Event</TableHead>
            <TableHead>Envelope</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Attempts</TableHead>
            <TableHead>When</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {deliveries.map((delivery) => {
            const status = STATUS_TONES[delivery.status]
            return (
              <TableRow
                key={delivery.id}
                data-record-id={delivery.id}
                className={cn('cursor-pointer', delivery.id === selectedId && 'bg-selected')}
                onClick={() => select(delivery.id)}
              >
                <TableCell>
                  <button type="button" className="font-mono text-caption text-link">
                    {delivery.event}
                  </button>
                </TableCell>
                <TableCell className="text-caption text-muted-foreground">
                  {delivery.envelopeId === null ? 'Organization' : <span className="font-mono">{delivery.envelopeId.slice(0, 8)}…</span>}
                </TableCell>
                <TableCell>
                  <Badge tone={status.tone}>{status.label}</Badge>
                  {delivery.lastStatusCode ? (
                    <span className="ml-2 text-caption text-muted-foreground">{delivery.lastStatusCode}</span>
                  ) : null}
                </TableCell>
                <TableCell className="text-right">{delivery.attemptCount}</TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {new Date(delivery.createdAt).toLocaleString()}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    )
  }

  function renderBody() {
    if (query.isLoading) return <LoadingState />
    if (query.isError || !query.data) {
      return <ErrorState message={apiErrorMessage(query.error, 'Could not load webhook deliveries.')} />
    }
    if (query.data.deliveries.length === 0 && !filters.before) {
      return (
        <EmptyState
          icon={Webhook}
          title="No deliveries"
          description={
            Object.keys(filters).length > 0
              ? 'Nothing matches these filters.'
              : `Webhooks appear here once ${scope.mode} envelopes start sending events to your webhook URL.`
          }
        />
      )
    }
    const nextBefore = query.data.nextBefore
    const deliveries = query.data.deliveries
    return (
      <div className="space-y-3">
        {renderList(deliveries)}
        <div className="flex justify-end gap-2">
          {filters.before ? (
            <Button variant="ghost" size="sm" onClick={() => setParam('before', null)}>
              Back to newest
            </Button>
          ) : null}
          {nextBefore ? (
            <Button variant="secondary" size="sm" onClick={() => setParam('before', nextBefore)}>
              Older deliveries
            </Button>
          ) : null}
        </div>
        <RecordSheet title="Webhook delivery" ids={deliveries.map((delivery) => delivery.id)} selectedId={selectedId} onSelect={select}>
          {(id) => (
            <DeliveryViewer id={id} canReplay={canAct} envelopeLink={(envelopeId) => `${scope.basePath}/envelopes/${envelopeId}`} />
          )}
        </RecordSheet>
      </div>
    )
  }

  return (
    <>
      <PageHeader
        title="Webhooks"
        description={`Every ${scope.mode} webhook PixelSign sent, newest first (100 at a time), with what your endpoint answered.`}
      />
      <ReadOnlyBanner />
      <div className="space-y-6">
        {scope.mode === 'test' && canAct ? <TestEventCard /> : null}
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Event" htmlFor="filter-event" className="w-52">
            <Select
              id="filter-event"
              value={filters.event ?? ''}
              onChange={(event) => {
                const next = webhookEventNameSchema.safeParse(event.target.value)
                setParam('event', next.success ? next.data : null)
              }}
            >
              <option value="">Any event</option>
              {WEBHOOK_EVENTS.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Status" htmlFor="filter-status" className="w-40">
            <Select
              id="filter-status"
              value={filters.status ?? ''}
              onChange={(event) => {
                const next = webhookDeliveryStatusSchema.safeParse(event.target.value)
                setParam('status', next.success ? next.data : null)
              }}
            >
              <option value="">Any status</option>
              <option value="delivered">Delivered</option>
              <option value="pending">Retrying</option>
              <option value="failed">Failed</option>
            </Select>
          </Field>
          {filters.envelopeId ? (
            <Button variant="ghost" size="sm" onClick={() => setParam('envelopeId', null)}>
              Clear envelope filter
            </Button>
          ) : null}
        </div>
        {renderBody()}
      </div>
    </>
  )
}
